import React, {
  createContext,
  useContext,
  useState,
} from "react";
import {
  FunctionField,
  List,
  TextField as RaTextField,
  useNotify,
  useRecordContext,
  useRefresh,
  useSidebarState,
  useTranslate,
} from "react-admin";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Edit as EditIcon,
  VpnKey as KeyIcon,
  BlockOutlined as BlockIcon,
  LockOpenOutlined as UnblockIcon,
  DeleteOutlined as DeleteIcon,
  MoreHoriz as MoreIcon,
} from "@mui/icons-material";
import {
  CustomDatagrid,
  FilterField,
  ListActionsBar,
} from "@/components/common";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { canEditUserRow, useCanEdit } from "@/utils/permissions";
import { getUserById } from "../services/userService";
import type { UserRecord } from "../types/user";
import UserFormDialog from "./UserFormDialog";
import CreateUserDialog from "./CreateUserDialog";
import { setUserEnabled, deleteUserHard } from "../services/userService";
import PasswordResetDialog from "./PasswordResetDialog";

const i18nBase = "resources.user-management";

interface RowActionContext {
  openEdit: (record: UserRecord) => void;
  openPasswordReset: (record: UserRecord) => void;
  toggleEnabled: (record: UserRecord) => void;
  openDelete: (record: UserRecord) => void;
}

const RowActionContext = createContext<RowActionContext | null>(null);
const useRowActions = (): RowActionContext => {
  const ctx = useContext(RowActionContext);
  if (!ctx) {
    throw new Error("Row action button used outside UserManagementList");
  }
  return ctx;
};

const UserRowMenu: React.FC = () => {
  const record = useRecordContext<UserRecord>();
  const translate = useTranslate();
  const { openEdit, openPasswordReset, toggleEnabled, openDelete } =
    useRowActions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  if (!record) return null;
  if (!canEditUserRow(record.role?.name)) return null;
  const blocked = record.isActive === false;
  const close = (): void => setAnchorEl(null);
  const item = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    danger = false,
  ): React.ReactNode => (
    <MenuItem
      onClick={(e) => {
        e.stopPropagation();
        close();
        onClick();
      }}
      sx={danger ? { color: "error.main" } : undefined}
    >
      <ListItemIcon sx={{ color: "inherit" }}>{icon}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
    </MenuItem>
  );
  return (
    <>
      <IconButton
        size="small"
        aria-label="row actions"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
      >
        {item(<EditIcon sx={{ fontSize: 18 }} />, translate("ra.action.edit"), () =>
          openEdit(record),
        )}
        {item(
          <KeyIcon sx={{ fontSize: 18 }} />,
          translate(`${i18nBase}.actions.changePassword`),
          () => openPasswordReset(record),
        )}
        {blocked
          ? item(
              <UnblockIcon sx={{ fontSize: 18 }} />,
              translate(`${i18nBase}.actions.unblock`),
              () => toggleEnabled(record),
            )
          : item(
              <BlockIcon sx={{ fontSize: 18 }} />,
              translate(`${i18nBase}.actions.block`),
              () => toggleEnabled(record),
            )}
        {item(
          <DeleteIcon sx={{ fontSize: 18 }} />,
          translate(`${i18nBase}.actions.deleteHard`),
          () => openDelete(record),
          true,
        )}
      </Menu>
    </>
  );
};

// Account users are created from the account wizard (Users step) — this
// page only lists and maintains them, so there is no Add button here.
const ListActions: React.FC = () => {
  const translate = useTranslate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterField
            source="name"
            label={translate(`${i18nBase}.filters.name`)}
          />
          <FilterField
            source="surname"
            label={translate(`${i18nBase}.filters.surname`)}
          />
          <FilterField
            source="email"
            label={translate(`${i18nBase}.filters.email`)}
          />
        </>
      }
    />
  );
};

export const UserManagementList: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserRecord | null>(null);
  const [rowBusy, setRowBusy] = useState(false);
  const [passwordResetEmail, setPasswordResetEmail] = useState<string | null>(
    null,
  );
  const canEdit = useCanEdit();

  const toggleEnabled = async (record: UserRecord): Promise<void> => {
    if (rowBusy) return;
    setRowBusy(true);
    try {
      await setUserEnabled(record.id, record.isActive === false);
      notify(
        `${i18nBase}.notifications.${record.isActive === false ? "unblocked" : "blocked"}`,
        { type: "success" },
      );
      refresh();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(err, "ra.notification.http_error", translate),
        { type: "error" },
      );
    } finally {
      setRowBusy(false);
    }
  };

  const confirmDeleteHard = async (): Promise<void> => {
    if (!deleteTarget || rowBusy) return;
    setRowBusy(true);
    try {
      await deleteUserHard(deleteTarget.id);
      notify(`${i18nBase}.notifications.deletedHard`, { type: "success" });
      setDeleteTarget(null);
      refresh();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(err, "ra.notification.http_error", translate),
        { type: "error" },
      );
    } finally {
      setRowBusy(false);
    }
  };

  const openEdit = async (record: UserRecord): Promise<void> => {
    try {
      const full = await getUserById(record.id);
      setEditingUser(full);
      setFormOpen(true);
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          `${i18nBase}.notifications.loadUserFailed`,
          translate,
        ),
        { type: "error" },
      );
    }
  };

  const openPasswordReset = (record: UserRecord): void => {
    setPasswordResetEmail(record.email);
  };

  const handlers: RowActionContext = { openEdit, openPasswordReset, toggleEnabled, openDelete: setDeleteTarget };

  return (
    <RowActionContext.Provider value={handlers}>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
            mt: 0,
            ml: 2,
          }}
        >
          {translate(`${i18nBase}.name`, { smart_count: 2 })}
          {canEdit && (
            <Button
              variant="contained"
              size="small"
              sx={{ ml: 2, verticalAlign: "middle" }}
              onClick={() => setCreateOpen(true)}
            >
              {translate(`${i18nBase}.actions.addUser`)}
            </Button>
          )}
        </Typography>
        <List
          actions={<ListActions />}
          sort={{ field: "0", order: "ASC" }}
          storeKey={false}
          disableSyncWithLocation
          perPage={10}
          exporter={false}
          sx={{
            maxWidth,
            overflow: "visible",
            "& .RaList-main, & .RaList-content": {
              overflow: "visible",
              width: "100%",
              maxWidth: "100%",
            },
            "& .RaListToolbar-root, & .RaList-actions": {
              display: "flex",
              width: "100%",
              mb: { xs: 1, sm: 2 },
              px: 0,
              py: { xs: 0, sm: 1 },
              minHeight: "auto",
            },
          }}
        >
          <CustomDatagrid
            primarySource="email"
            bulkActionButtons={false}
            disableMobileView
          >
            {canEdit && (
              <FunctionField
                label={translate(`${i18nBase}.columns.actions`)}
                render={() => <UserRowMenu />}
              />
            )}
            <RaTextField source="name" />
            <RaTextField source="surname" />
            <RaTextField source="email" />
            <FunctionField
              source="accountName"
              label={translate(`${i18nBase}.fields.account`)}
              render={(record: UserRecord) => {
                const names =
                  record.accountNames && record.accountNames.length > 0
                    ? record.accountNames
                    : record.accountName
                      ? [record.accountName]
                      : [];
                if (names.length === 0) return "—";
                const extra = names.length - 1;
                return (
                  <Box
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
                  >
                    <span>{names[0]}</span>
                    {extra > 0 && (
                      <Tooltip title={names.join(", ")}>
                        <Chip
                          label={`+${extra}`}
                          size="small"
                          sx={{ height: 20, fontSize: 11, cursor: "default" }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                );
              }}
            />
            <FunctionField
              source="isActive"
              label={translate(`${i18nBase}.fields.status`)}
              render={(record: UserRecord) =>
                record.isActive === false ? (
                  <Chip
                    size="small"
                    color="warning"
                    label={translate(`${i18nBase}.status.blocked`)}
                  />
                ) : (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={translate(`${i18nBase}.status.active`)}
                  />
                )
              }
            />
            <FunctionField
              source="canEditKnowledgeBase"
              label={translate(`${i18nBase}.fields.aiCapabilities`)}
              render={(record: UserRecord) => (
                <Box sx={{ display: "inline-flex", gap: 0.5 }}>
                  {/* Sin distintivo de IA por usuario: la IA se decide por CUENTA
                      (ver UserFormDialog). Mostrarlo hacía creer que el usuario la tenía. */}
                  {record.canEditKnowledgeBase && (
                    <Tooltip title={translate(`${i18nBase}.fields.canEditKnowledgeBase`)}>
                      <Chip
                        size="small"
                        color="secondary"
                        label={translate(`${i18nBase}.capabilities.kb`)}
                        sx={{ height: 20, fontSize: 11, cursor: "default" }}
                      />
                    </Tooltip>
                  )}
                </Box>
              )}
            />
          </CustomDatagrid>
        </List>

        <UserFormDialog
          record={editingUser}
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSuccess={() => {
            setFormOpen(false);
            refresh();
          }}
        />
        <PasswordResetDialog
          email={passwordResetEmail}
          onClose={() => setPasswordResetEmail(null)}
          onSuccess={() => setPasswordResetEmail(null)}
        />
        <Dialog
          open={deleteTarget !== null}
          onClose={() => !rowBusy && setDeleteTarget(null)}
          PaperProps={{ sx: { width: 440, maxWidth: "90vw" } }}
        >
          <DialogContent sx={{ textAlign: "center", pt: "24px !important" }}>
            <Typography sx={{ fontSize: "14.5px", fontWeight: 700, mb: 1 }}>
              {translate(`${i18nBase}.deleteHard.title`, {
                email: deleteTarget?.email ?? "",
              })}
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: "text.secondary", lineHeight: 1.55 }}>
              {translate(`${i18nBase}.deleteHard.content`)}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button disabled={rowBusy} onClick={() => setDeleteTarget(null)}>
              {translate("ra.action.cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={rowBusy}
              onClick={() => void confirmDeleteHard()}
            >
              {translate("ra.action.delete")}
            </Button>
          </DialogActions>
        </Dialog>

        <CreateUserDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSuccess={() => {
            setCreateOpen(false);
            refresh();
          }}
        />
      </Box>
    </RowActionContext.Provider>
  );
};

export default UserManagementList;
